import React, { useEffect, useState } from 'react';
import { Plus, ArrowDownCircle, Pencil, Trash2, Eye } from 'lucide-react';
import { Card, Btn, Input, Field, Modal, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { inventoryAPI, masterAPI } from '../../../utils/api';
import { fmtRp, fmtDate } from '../../../utils/format';

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

export default function ReceiptTab() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [refAkun, setRefAkun] = useState([]);
  const [form, setForm] = useState({ receipt_date: new Date().toISOString().split('T')[0], delivery_date: '', bast_date: '', bast_no: '', supplier_name: '', vendor_id: '', purchase_order_ref: '', items: [] });
  const [currentItem, setCurrentItem] = useState({ item_id: '', warehouse_id: '', quantity: 0, unit_cost: 0, akun_id: '' });

  useEffect(() => { loadReceipts(); loadRefs(); }, []);

  const loadRefs = async () => {
    try {
      const [locs, itm, ak] = await Promise.all([
        inventoryAPI.getLocations(),
        inventoryAPI.getItems('?limit=100'),
        masterAPI.getCoaAkun(),
      ]);
      setLocations((locs.data || []).map(x => ({ value: x.id, label: x.nama })));
      setItems((itm.data || []).map(x => ({ value: x.id, label: x.nama })));
      setRefAkun((ak.data||[]).map(x=>({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
      })));
    } catch {}
  };

  const loadReceipts = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await inventoryAPI.getReceipts();
      setReceipts(r.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleAddItem = () => {
    if (!currentItem.item_id || !currentItem.warehouse_id || !currentItem.quantity) return alert('Barang, lokasi, dan qty wajib diisi');
    setForm({ ...form, items: [...form.items, { ...currentItem }] });
    setCurrentItem({ item_id: '', warehouse_id: '', quantity: 0, unit_cost: 0, akun_id: '' });
  };

  const handleRemoveItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const calculateTotal = () => form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_cost || 0), 0);

  const handleSave = async () => {
    if (!form.receipt_date) return alert('Tanggal wajib diisi');
    if (form.items.length === 0) return alert('Minimal 1 item harus ditambahkan');
    setSaving(true);
    try {
      await inventoryAPI.createReceipt(form);
      closeModal();
      loadReceipts();
    } catch (e) { alert('Gagal menyimpan: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleView = async (id) => {
    try {
      const r = await inventoryAPI.getReceipt(id);
      setViewReceipt(r.data);
    } catch (e) { alert(e.message); }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ receipt_date: new Date().toISOString().split('T')[0], delivery_date: '', bast_date: '', bast_no: '', supplier_name: '', vendor_id: '', purchase_order_ref: '', items: [] });
    setCurrentItem({ item_id: '', warehouse_id: '', quantity: 0, unit_cost: 0, akun_id: '' });
  };

  const selectedAkunItem = refAkun.find(x => x.value === currentItem.akun_id);

  const getStatusBadge = (status) => {
    const cfg = { DRAFT: { bg: '#F3F4F6', color: '#374151' }, DISETUJUI: { bg: '#ECFDF5', color: '#059669' }, DITOLAK: { bg: '#FEF2F2', color: '#DC2626' } };
    const c = cfg[status] || cfg.DRAFT;
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>;
  if (error) return <div style={{ marginBottom: 16 }}><ErrBox msg={error} onRetry={loadReceipts} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Penerimaan Baru</Btn>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <ArrowDownCircle size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Riwayat Penerimaan ({receipts.length})
          </h3>
        </div>
        {receipts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <ArrowDownCircle size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Belum ada data penerimaan</p>
          </div>
        ) : (
          <Table pageSize={10} cols={[
            { key: 'receipt_no', label: 'No. Receipt', nowrap: true },
            { key: 'receipt_date', label: 'Tanggal', render: v => fmtDate(v) },
            { key: 'supplier_name', label: 'Supplier' },
            { key: 'total_items', label: 'Items', align: 'center' },
            { key: 'total_cost', label: 'Total', align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1E40AF' }}>{fmtRp(v)}</span> },
            { key: 'status', label: 'Status', render: v => getStatusBadge(v) },
            { key: 'id', label: 'Aksi', render: (id) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="secondary" icon={<Eye size={12}/>} onClick={() => handleView(id)}>Detail</Btn>
              </div>
            )},
          ]} data={receipts} />
        )}
      </Card>

      {showModal && (
        <Modal title="Penerimaan Barang Baru" onClose={closeModal} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Tanggal" required>
              <Input type="date" value={form.receipt_date} onChange={e => setForm({ ...form, receipt_date: e.target.value })} />
            </Field>
            <Field label="Supplier">
              <Input value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} placeholder="Nama supplier" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="No. PO">
              <Input value={form.purchase_order_ref} onChange={e => setForm({ ...form, purchase_order_ref: e.target.value })} placeholder="PO-..." />
            </Field>
            <Field label="No. BAST">
              <Input value={form.bast_no} onChange={e => setForm({ ...form, bast_no: e.target.value })} placeholder="BAST-..." />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Tanggal Delivery">
              <Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} />
            </Field>
            <Field label="Tanggal BAST">
              <Input type="date" value={form.bast_date} onChange={e => setForm({ ...form, bast_date: e.target.value })} />
            </Field>
          </div>

          <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, margin: '16px 0' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#374151' }}>Tambah Item</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
              <Field label="Barang">
                <Select value={currentItem.item_id} onChange={e => setCurrentItem({ ...currentItem, item_id: e.target.value })} options={items} placeholder="Pilih..." />
              </Field>
              <Field label="Gudang">
                <Select value={currentItem.warehouse_id} onChange={e => setCurrentItem({ ...currentItem, warehouse_id: e.target.value })} options={locations} placeholder="Pilih..." />
              </Field>
              <Field label="Akun">
                <Select value={currentItem.akun_id} onChange={e => setCurrentItem({ ...currentItem, akun_id: e.target.value })} options={refAkun} placeholder="Pilih..." />
              </Field>
              <Field label="Qty">
                <Input type="number" value={currentItem.quantity || ''} onChange={e => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) || 0 })} />
              </Field>
              <Field label="Biaya/Unit">
                <Input type="number" value={currentItem.unit_cost || ''} onChange={e => setCurrentItem({ ...currentItem, unit_cost: parseFloat(e.target.value) || 0 })} />
              </Field>
              <Btn size="sm" onClick={handleAddItem}>+ Tambah</Btn>
            </div>
            {selectedAkunItem && (
              <div style={{ marginTop:8, padding:8, background:"#EEF2FF", borderRadius:6, fontSize:11, display:"flex", gap:4, flexWrap:"wrap" }}>
                <ProgramBadge v={selectedAkunItem.program_kode}/>
                <KegiatanBadge v={selectedAkunItem.kegiatan_kode}/>
                <KroBadge v={selectedAkunItem.kro_kode}/>
              </div>
            )}
          </div>

          {form.items.length > 0 && (
            <Card style={{ margin: '12px 0', padding: 0 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 13 }}>Items ({form.items.length})</strong>
                <strong style={{ color: '#1E40AF' }}>Total: {fmtRp(calculateTotal())}</strong>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#F8FAFC' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Barang</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Gudang</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Biaya</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Subtotal</th>
                  <th style={{ padding: '8px 12px', width: 50 }}></th>
                </tr></thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const itemData = items.find(i => i.value === item.item_id);
                    const locData = locations.find(l => l.value === item.warehouse_id);
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '8px 12px' }}>{itemData?.label}</td>
                        <td style={{ padding: '8px 12px' }}>{locData?.label}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{item.quantity}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtRp(item.unit_cost)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>{fmtRp(item.quantity * item.unit_cost)}</td>
                        <td style={{ padding: '8px 12px' }}><Btn size="sm" variant="danger" onClick={() => handleRemoveItem(idx)}>X</Btn></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</Btn>
          </div>
        </Modal>
      )}

      {viewReceipt && (
        <Modal title={`Detail Receipt ${viewReceipt.receipt_no}`} onClose={() => setViewReceipt(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Tanggal</strong><p style={{ margin: 2 }}>{fmtDate(viewReceipt.receipt_date)}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Supplier</strong><p style={{ margin: 2 }}>{viewReceipt.supplier_name || '-'}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>No. BAST</strong><p style={{ margin: 2 }}>{viewReceipt.bast_no || '-'}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Total Cost</strong><p style={{ margin: 2, fontFamily: 'monospace', fontWeight: 600, color: '#1E40AF' }}>{fmtRp(viewReceipt.total_cost)}</p></div>
          </div>
          {viewReceipt.details?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F8FAFC' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Barang</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty Received</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Unit Cost</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Subtotal</th>
              </tr></thead>
              <tbody>
                {viewReceipt.details.map((d, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 12px' }}>{d.item_nama}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{d.quantity_received}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtRp(d.unit_cost)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtRp(d.quantity_received * d.unit_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setViewReceipt(null)}>Tutup</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}